import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Link as MuiLink, Box, Typography, Stack } from "@mui/material";

import { syncMFA } from "../../action/auth";
import customConfig from "../../config";

import Dialog from "../Common/Dailog/Dailog";
import AppButton from "../Common/Button/Button";
import FormInput from "../Common/FormInputs/MuiTextField";
import Alert from "../layout/Alert";

const formMaxWidth = 360;

const CodeInput = ({ syncMFA: syncMFAAction }) => {
  const [code1, setCode1] = useState("");
  const [code2, setCode2] = useState("");
  const [errors, setErrors] = useState({});
  const [showInstructions, setShowInstructions] = useState(false);

  const closeInstructions = () => setShowInstructions(false);
  const handleInstructionsClick = (e) => {
    e?.preventDefault?.();
    setShowInstructions(true);
  };

  const canSubmit = useMemo(() => {
    return Boolean(code1) && Boolean(code2);
  }, [code1, code2]);

  const handleSubmit = (e) => {
    e?.preventDefault?.();

    const validationErrors = {};
    if (!code1) validationErrors.code1 = "Code 1 is required";
    if (!code2) validationErrors.code2 = "Code 2 is required";
    if (code1 && code2 && code1 === code2) {
      validationErrors.code2 = "Code 2 must be different from Code 1";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    syncMFAAction({ code1, code2 })
      .then(() => {
        setCode1("");
        setCode2("");
        setErrors({});
      })
      .catch(() => {
        setErrors(customConfig.mfaSyncError || {});
      });
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Typography
        variant="h6"
        sx={{
          textAlign: "center",
          whiteSpace: "nowrap",
          maxWidth: { xs: "100%", sm: formMaxWidth },
          mx: "auto",
          mb: 2,
          fontSize: "clamp(0.72rem, 2.9vw, 0.95rem)",
          lineHeight: 1.2,
        }}
      >
        Enter codes to sync 2-Step Authentication.
      </Typography>

      <Alert />

      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: "100%" }}>
        <Stack
          spacing={2}
          sx={{
            width: "100%",
            maxWidth: { xs: "100%", sm: formMaxWidth },
            mx: "auto",
            px: { xs: 0, sm: 0 },
          }}
        >
          <Box sx={{ width: "100%" }}>
            <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600 }}>
              Code 1 *
            </Typography>
            <FormInput
              id="code1"
              name="code1"
              type="text"
              value={code1}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d{0,6}$/.test(value)) setCode1(value);
              }}
              placeholder="Enter Code 1"
              inputProps={{ maxLength: 6, inputMode: "numeric", pattern: "[0-9]*" }}
              required
              error={Boolean(errors.code1)}
              helperText={errors.code1 || ""}
              touched="true"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": { overflow: "visible" },
              }}
            />
          </Box>

          <Box sx={{ width: "100%" }}>
            <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600 }}>
              Code 2 *
            </Typography>
            <FormInput
              id="code2"
              name="code2"
              type="text"
              value={code2}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d{0,6}$/.test(value)) setCode2(value);
              }}
              placeholder="Enter Code 2"
              inputProps={{ maxLength: 6, inputMode: "numeric", pattern: "[0-9]*" }}
              required
              error={Boolean(errors.code2)}
              helperText={errors.code2 || ""}
              touched="true"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": { overflow: "visible" },
              }}
            />
          </Box>

          <MuiLink
            component="button"
            type="button"
            onClick={handleInstructionsClick}
            underline="none"
            sx={{
              color: "primary.main",
              fontWeight: 500,
              textAlign: "center",
              display: "block",
              width: "100%",
              backgroundColor: "transparent",
              border: 0,
              padding: 0,
              margin: 0,
              lineHeight: 1.2,
              cursor: "pointer",
              "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
              "&:focus": { backgroundColor: "transparent" },
              "&:focus-visible": { outline: "2px solid rgba(25, 118, 210, 0.6)", outlineOffset: 2 },
            }}
          >
            Need help entering codes?
          </MuiLink>

          <Box sx={{ width: "100%" }}>
            <AppButton
              type="submit"
              variant="primary"
              size="large"
              fullWidth
              disabled={!canSubmit}
              sx={{
                mt: 0,
                height: 50,
                fontSize: 17,
                fontWeight: 600,
                width: "100%",
              }}
            >
              Submit
            </AppButton>
          </Box>
        </Stack>
      </Box>

      <Dialog
        open={showInstructions}
        onClose={closeInstructions}
        maxWidth="sm"
        fullWidth
        showCloseButton
        closeOnBackdropClick
        closeOnEscapeKeyDown
        contentSx={{
          pt: 0,
          pb: 2,
          overflow: "visible",
        }}
        title={undefined}
        PaperProps={{
          sx: { borderRadius: 2, width: 680, maxWidth: "calc(100vw - 24px)" },
        }}
      >
        <div className="qr-popup-container" style={{ maxWidth: "100%" }}>
          <h2
            style={{
              overflow: "visible",
              maxWidth: "100%",
              fontSize: "clamp(20px, 5vw, 24px)",
              lineHeight: 1.2,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            How to Enter 2-Step Authentication Codes
          </h2>
          <ol>
            {(customConfig.mfaCodeInstructions || []).map((instruction, index) => (
              <li key={index}>{instruction}</li>
            ))}
          </ol>
        </div>
      </Dialog>
    </Box>
  );
};

CodeInput.propTypes = {
  syncMFA: PropTypes.func.isRequired,
};

const mapDispatchToProps = (dispatch) => ({
  syncMFA: (codes) => dispatch(syncMFA(codes)),
});

export default connect(null, mapDispatchToProps)(CodeInput);

